import { useState, type ReactNode } from 'react';
import { Link, Navigate } from 'react-router';
import { AppFooter } from '../components/AppFooter';
import { Alert, Loading } from '../components/ui';
import { useI18n } from '../i18n';
import { useMe } from '../session';
import { ConsoleHeader } from './ConsolePage';
import { Ph, Say, SectionBar, useSalesPageBackground } from './sales-ui';

// The call guide for sellers (Outils de vente → Guide d'appel): the first contact with a board member, the arguments
// and offers, objections, voicemail and emails, qualification, discovery and referrals. This page is the reference
// text (docs/sales/scripts.md points here): update it here, then deploy.

const SECTIONS: [string, string][] = [
  ['regles', 'Règles'],
  ['preparer', 'Préparer'],
  ['structure', 'Structure'],
  ['scripts', 'Scripts'],
  ['arguments', 'Arguments'],
  ['offres', 'Offres'],
  ['objections', 'Objections'],
  ['messages', 'Messages'],
  ['qualifier', 'Qualifier'],
  ['decouverte', 'Découverte'],
  ['references', 'Références'],
];

const SITUATIONS: { key: string; label: string; body: ReactNode }[] = [
  {
    key: 'a',
    label: 'A · Recommandation',
    body: (
      <>
        <h3>Ouverture</h3>
        <Say>
          « Bonjour madame <Ph>[Nom]</Ph>, ici <Ph>[Prénom]</Ph> de Syndiqia. C’est <Ph>[Marc Tremblay, votre déneigeur]</Ph> qui m’a suggéré de vous appeler : il m’a dit que vous êtes{' '}
          <Ph>[présidente]</Ph> de l’association du <Ph>[Domaine X]</Ph>. Avez-vous cinq minutes, ou je vous rappelle à un meilleur moment? »
        </Say>
        <p>Puis les trois questions, le pont, et la demande :</p>
        <Say>
          « Ce que je vous propose : une rencontre d’une heure avec vous et une autre personne du conseil. Je vous montre ce que Syndiqia ferait concrètement pour le <Ph>[Domaine X]</Ph>, avec un domaine
          de démonstration qui ressemble au vôtre. Si ça ne vous convient pas, on en reste là. Est-ce que <Ph>[mardi soir]</Ph> ou <Ph>[jeudi en fin de journée]</Ph> serait possible? »
        </Say>
      </>
    ),
  },
  {
    key: 'b',
    label: 'B · Suivi de lettre',
    body: (
      <>
        <h3>Ouverture</h3>
        <Say>
          « Bonjour monsieur <Ph>[Nom]</Ph>, ici <Ph>[Prénom]</Ph> de Syndiqia. Je vous ai écrit la semaine dernière au sujet du diagnostic gratuit pour l’association du <Ph>[Domaine X]</Ph>. Est-ce que la
          lettre s’est rendue? »
        </Say>
        <p>
          <strong>Si oui :</strong> « Qu’est-ce qui vous a fait réagir, ou pas? » puis les trois questions.
        </p>
        <p>
          <strong>Si non :</strong>
        </p>
        <Say>
          <p>« En deux mots : vous nous envoyez vos règlements, et en 48 heures on vous remet une page avec vos règles d’assemblée, vos échéances de l’année et trois points à vérifier. C’est gratuit et sans engagement. Je peux vous l’envoyer par courriel? »</p>
          <p>« Et pendant que je vous ai : qui s’occupe de l’administration aujourd’hui? »</p>
        </Say>
      </>
    ),
  },
  {
    key: 'c',
    label: 'C · Demande entrante',
    body: (
      <>
        <h3>Ouverture</h3>
        <Say>
          « Bonjour madame <Ph>[Nom]</Ph>, ici <Ph>[Prénom]</Ph> de Syndiqia. Vous avez <Ph>[demandé le diagnostic / téléchargé le modèle d’avis de convocation]</Ph> hier; je voulais m’assurer que vous avez
          bien reçu ce qu’il vous fallait. C’est pour l’AGA du <Ph>[Domaine X]</Ph>? »
        </Say>
        <p>La personne a déjà un besoin : aller directement au contexte (« Comment se prépare votre AGA, d’habitude? »), puis à la demande.</p>
      </>
    ),
  },
  {
    key: 'd',
    label: 'D · Après un événement',
    body: (
      <>
        <h3>Ouverture</h3>
        <Say>
          « Bonjour monsieur <Ph>[Nom]</Ph>, ici <Ph>[Prénom]</Ph>. On s’est parlé à la soirée des administrateurs à <Ph>[Sainte-Agathe]</Ph> mardi; vous m’aviez parlé de <Ph>[la perception des cotisations]</Ph>.
          Je vous appelle comme promis. »
        </Say>
        <p>Puis reprendre leur irritant, une ou deux questions de contexte, et la demande.</p>
      </>
    ),
  },
  {
    key: 'e',
    label: 'E · Sans contact préalable',
    body: (
      <>
        <h3>L’exception : court, et la permission d’abord</h3>
        <Say>
          « Bonjour madame <Ph>[Nom]</Ph>, ici <Ph>[Prénom]</Ph> de Syndiqia. Je vous appelle sans recommandation, je vais faire vite : on aide les associations de propriétaires de la région à gérer leur
          administration (cotisations, déneigement, assemblées). J’ai vu que <Ph>[le Domaine X vient de se constituer en OBNL]</Ph>. Est-ce un sujet pour votre conseil en ce moment? »
        </Say>
        <p>
          <strong>Si non :</strong> remercier, proposer le diagnostic par courriel, raccrocher. Ne pas insister.
        </p>
      </>
    ),
  },
];

const MAILS = [
  {
    id: 'rencontre',
    title: 'Courriel : rencontre fixée',
    text: `Objet : Notre rencontre du [date] – [Domaine X]

Bonjour [Prénom],

Merci pour votre temps aujourd’hui. Comme convenu, on se voit le [date] à [heure], [lieu / par vidéo : lien], avec [nom de l’autre administrateur].

Vous m’avez parlé surtout de [irritant, avec leurs mots]. Je vous montrerai concrètement ce que Syndiqia ferait pour ça, avec un domaine de démonstration semblable au vôtre.

Si vous voulez jeter un œil avant : https://app.syndiqia.com/about. Et si vous pouvez m’envoyer vos règlements, je pourrai vous montrer une réponse tirée de vos propres règles (seulement si vous le souhaitez).

Au plaisir,
[Prénom Nom], Syndiqia, [téléphone]`,
  },
  {
    id: 'diagnostic',
    title: 'Courriel : pas de rencontre, diagnostic proposé',
    text: `Objet : Le diagnostic gratuit pour le [Domaine X]

Bonjour [Prénom],

Merci pour l’échange. Si ça vous intéresse, envoyez-moi simplement vos règlements (et vos actes de servitude si vous les avez) en réponse à ce courriel. En 48 heures, vous recevrez une page : vos règles d’assemblée, vos échéances de l’année et trois points à vérifier. C’est gratuit, et les documents sont supprimés après 30 jours si vous n’allez pas plus loin.

[Prénom Nom], Syndiqia, [téléphone]`,
  },
];

const CRITERIA: [string, string, string][] = [
  ['size', 'Taille', 'Entre 15 et 300 lots'],
  ['person', 'Bonne personne', 'On a parlé à un administrateur, idéalement président ou trésorier'],
  ['pain', 'Irritant clair', 'La personne a nommé un problème précis'],
  ['date', 'Échéance', 'AGA, fin de contrat, départ d’un administrateur, nouvelle règle'],
  ['open', 'Ouverture', 'Accepte une rencontre ou le diagnostic'],
];

function CopyButton({ text }: { text: string }) {
  const [state, setState] = useState<'idle' | 'done' | 'failed'>('idle');
  const copy = () => {
    navigator.clipboard.writeText(text).then(
      () => setState('done'),
      () => setState('failed'),
    );
    setTimeout(() => setState('idle'), 1800);
  };
  return (
    <button type="button" onClick={copy}>
      {state === 'done' ? 'Copié' : state === 'failed' ? 'Sélectionnez le texte' : 'Copier'}
    </button>
  );
}

function Score() {
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const n = CRITERIA.filter(([k]) => checked[k]).length;
  const verdict =
    n >= 4
      ? { tone: 'go', label: 'Rencontre en priorité', text: 'Fixer la rencontre de découverte rapidement.' }
      : n >= 2
        ? { tone: 'later', label: 'Diagnostic et rappel', text: 'Proposer le diagnostic, et rappeler à l’échéance.' }
        : { tone: 'stop', label: 'Ne pas relancer', text: 'Remercier, noter, et ne pas relancer avant un déclencheur.' };
  return (
    <div className="cg-score">
      {CRITERIA.map(([k, title, hint]) => (
        <label key={k}>
          <input type="checkbox" checked={!!checked[k]} onChange={(e) => setChecked({ ...checked, [k]: e.target.checked })} />
          <span>
            <strong>{title}</strong>
            <span className="muted small">{hint}</span>
          </span>
        </label>
      ))}
      <div className="cg-result" aria-live="polite">
        <span className="cg-num">{n} / 5</span>
        <span className={`cg-pill cg-${verdict.tone}`}>{verdict.label}</span>
        <span>{verdict.text}</span>
      </div>
    </div>
  );
}

export function CallGuidePage() {
  const { t } = useI18n();
  const me = useMe();
  const [situation, setSituation] = useState('a');
  useSalesPageBackground();
  if (me.isPending) return <Loading label={t.loading} />;
  if (me.isError) return <Alert>{t.common.error}</Alert>;
  if (!me.data.staff.includes('platform_admin') && !me.data.staff.includes('sales')) return <Navigate to="/" replace />;
  const current = SITUATIONS.find((s) => s.key === situation)!;

  return (
    <div className="chooser console cg">
      <ConsoleHeader me={me.data} />
      <p>
        <Link to="/ventes">← {t.salesTools.title}</Link>
      </p>
      <p className="cg-eyebrow">Syndiqia · ventes</p>
      <h1>Guide d’appel</h1>
      <p className="cg-lede">
        La trame du premier contact avec un administrateur, les arguments et les offres qui accrochent, puis la découverte. À garder ouvert pendant l’appel. Dites-le avec vos mots, mais gardez l’ordre. Les
        passages <Ph>surlignés</Ph> sont à remplacer.
      </p>

      <SectionBar sections={SECTIONS} />

      <section id="regles">
        <h2>Le premier appel en trois règles</h2>
        <div className="cg-rules">
          <div><strong>Une raison d’appeler, en une phrase</strong>Un nom, une lettre envoyée, une demande reçue, une soirée. Jamais « je vous présente notre plateforme ».</div>
          <div><strong>Leur situation avant notre produit</strong>Trois questions d’abord. Ensuite seulement, le lien entre ce qu’ils vivent et ce que Syndiqia change.</div>
          <div><strong>Une seule demande, sans risque</strong>Une rencontre de 60 minutes avec un ou deux administrateurs, ou le diagnostic gratuit. On ne vend rien au téléphone.</div>
        </div>
        <p className="muted small">Durée visée : 10 à 15 minutes. L’appel est réussi quand une date est fixée.</p>
      </section>

      <section id="preparer">
        <h2>Avant d’appeler (2 minutes)</h2>
        <ul>
          <li><strong>Qui :</strong> nom, rôle au conseil (président, trésorier, secrétaire), association, nombre de lots.</li>
          <li><strong>D’où vient le contact :</strong> qui nous recommande, quelle lettre, quel événement, quelle demande.</li>
          <li><strong>Ce qu’on sait déjà :</strong> site ou page Facebook du domaine, fiche du Registraire, déclencheur (règle municipale sur le chemin, firme qui augmente ses frais, trésorier qui part, AGA qui approche).</li>
          <li><strong>Choisir</strong> l’accroche (<a href="#arguments">Arguments</a>) et l’offre (<a href="#offres">Offres</a>).</li>
        </ul>
      </section>

      <section id="structure">
        <h2>La structure de l’appel</h2>
        <div className="cg-steps">
          {[
            ['20 s', 'Ouverture.', 'Se nommer, dire pourquoi on appelle, demander si c’est un bon moment.'],
            ['3 min', 'Contexte.', 'Trois questions; écouter, noter leurs mots exacts.'],
            ['1 min', 'Pont.', 'Reformuler leur irritant, dire en une phrase ce que Syndiqia change.'],
            ['1 min', 'Demande.', 'La rencontre (ou le diagnostic), avec une date.'],
            ['30 s', 'Conclusion.', 'Qui, quand, où; annoncer le courriel récapitulatif.'],
          ].map(([time, title, text]) => (
            <div key={title}>
              <span className="cg-time">{time}</span>
              <span><strong>{title}</strong> {text}</span>
            </div>
          ))}
        </div>
        <h3>Les trois questions de contexte</h3>
        <ol>
          <li>« Aujourd’hui, qui s’occupe de l’administration de l’association : des bénévoles, une comptable, une firme? »</li>
          <li>« Qu’est-ce qui vous prend le plus de temps, ou qui vous inquiète le plus? »</li>
          <li>« S’il y avait une chose à régler cette année, ce serait laquelle? »</li>
        </ol>
        <p className="muted small">Relance selon la réponse 1 : « La firme, vous en êtes satisfaits? Le contrat se termine quand? » ou « Combien d’heures par mois, à peu près, pour les bénévoles? »</p>
        <h3>Le pont</h3>
        <Say>
          « Si je comprends bien, c’est surtout <Ph>[la perception des cotisations]</Ph> qui vous pèse. Syndiqia, c’est une équipe et des assistants qui font ce travail-là pour le conseil : ils préparent, et
          c’est le conseil qui approuve. Vous gardez les décisions, on enlève la paperasse. »
        </Say>
      </section>

      <section id="scripts">
        <h2>Scripts selon la situation</h2>
        <div className="cg-tabs" role="tablist" aria-label="Situation">
          {SITUATIONS.map((s) => (
            <button key={s.key} type="button" role="tab" aria-selected={s.key === situation} onClick={() => setSituation(s.key)}>
              {s.label}
            </button>
          ))}
        </div>
        <div role="tabpanel" aria-label={current.label}>{current.body}</div>
      </section>

      <section id="arguments">
        <h2>Les bons arguments</h2>
        <h3>Selon le rôle</h3>
        <div className="cg-table">
          <table>
            <thead>
              <tr><th>Interlocuteur</th><th>Ce qui le préoccupe</th><th>Accroche</th><th>À montrer en rencontre</th></tr>
            </thead>
            <tbody>
              <tr><td>Président·e</td><td>Responsabilité, assemblées, conflits, bénévoles épuisés</td><td>« Le conseil décide, Syndiqia prépare et fait le suivi. »</td><td>Avis de convocation avec preuve de livraison, procès-verbal rédigé, registre des résolutions</td></tr>
              <tr><td>Trésorier·ère</td><td>Cotisations en retard, banque, budget, fin d’année</td><td>« Les rappels sont préparés, la banque se concilie en quelques minutes, la comptable reçoit un dossier propre. »</td><td>Rappels à approuver, conciliation bancaire, fonds séparés, factures associées aux bons de travail</td></tr>
              <tr><td>Secrétaire</td><td>Procès-verbaux, courriels des propriétaires, archives</td><td>« Les procès-verbaux se rédigent à partir de l’enregistrement; les questions reçoivent un brouillon tiré de vos règlements. »</td><td>Demande reçue par courriel avec réponse citant l’article; procès-verbal à partir de l’enregistrement</td></tr>
              <tr><td>Nouvel administrateur</td><td>Ne sait pas par où commencer</td><td>« Tout le savoir du conseil au même endroit, avec le calendrier de l’année. »</td><td>Calendrier des obligations, documents, historique</td></tr>
            </tbody>
          </table>
        </div>
        <h3>Selon la situation</h3>
        <div className="cg-table">
          <table>
            <thead>
              <tr><th>Situation</th><th>L’argument qui porte</th><th>L’offre</th></tr>
            </thead>
            <tbody>
              <tr><td>Autogérée, bénévoles débordés</td><td>Le travail est fait, pas seulement un logiciel de plus</td><td>Rencontre, puis essai gratuit</td></tr>
              <tr><td>Sous contrat avec une firme insatisfaisante</td><td>Prix publié 25 à 45 % sous les firmes, aucuns frais de sortie, prix garanti, transition gérée</td><td>Essai en parallèle pendant le préavis</td></tr>
              <tr><td>Nouvelle association (chemin privé, nouveau domaine)</td><td>Tout en place dès le départ : règlements, cotisations, déneigement, première AGA</td><td>AGA gratuite, mise en service</td></tr>
              <tr><td>Syndicat soumis à la loi 16</td><td>Carnet d’entretien alimenté, calendrier des obligations, fonds de prévoyance suivi, avec les professionnels</td><td>Rencontre centrée sur la conformité</td></tr>
            </tbody>
          </table>
        </div>
        <h3>Les forces à répéter (toutes vraies aujourd’hui)</h3>
        <ul>
          <li><strong>Ça fait le travail :</strong> les assistants préparent les rappels, les réponses, les procès-verbaux, les bons de travail.</li>
          <li><strong>Le conseil garde la main :</strong> tout commence en mode confirmation; rien de conséquent ne part sans approbation.</li>
          <li><strong>Fait pour le Québec, en français :</strong> vocabulaire et règles d’assemblée selon leurs règlements.</li>
          <li><strong>Données hébergées au Canada,</strong> accès par rôle, journal infalsifiable, exportation en tout temps.</li>
          <li><strong>Simple pour les membres :</strong> tout fonctionne par courriel; l’application est facultative.</li>
          <li><strong>Prix publiés,</strong> pas de frais de sortie, prix garanti 24 mois.</li>
        </ul>
        <h3>À ne pas dire</h3>
        <ul className="cg-dont">
          <li>Un chiffre non mesuré (« vous sauverez 20 heures par mois »). Dire : « on le mesure ensemble pendant l’essai ».</li>
          <li>« L’IA remplace le conseil » ou « plus besoin de gestionnaire ».</li>
          <li>Un avis juridique (« votre règlement est invalide »). Le diagnostic est un résumé.</li>
          <li>Du mal de la firme actuelle. On compare des services et des prix, pas des gens.</li>
        </ul>
      </section>

      <section id="offres">
        <h2>L’échelle des offres</h2>
        <p>Proposer la plus petite étape qui mène à la suivante. Chacune donne de la valeur sans engagement.</p>
        <ol className="cg-ladder">
          <li><strong>Diagnostic gratuit</strong> : règlements résumés en 48 h<span>Engagement : envoyer un document. Au premier contact, si la rencontre est prématurée.</span></li>
          <li><strong>Rencontre de découverte</strong> (60 min)<span>Engagement : une heure, un ou deux administrateurs. La demande principale du premier appel.</span></li>
          <li><strong>Démo à tout le conseil</strong> (90 min)<span>Engagement : une soirée du conseil. À la fin de la découverte.</span></li>
          <li><strong>AGA gratuite</strong><span>Engagement : laisser Syndiqia organiser une assemblée. Nouvelle association, AGA proche, secrétaire qui part.</span></li>
          <li><strong>Essai gratuit de 3 mois</strong> après la mise en service<span>Engagement : une page signée, frais de mise en service reportés. À la fin de la démo au conseil.</span></li>
          <li><strong>Programme clients fondateurs</strong><span>Trois mois gratuits et prix garanti trois ans, en échange d’une étude de cas, de références et d’une rétroaction mensuelle. En conclusion de démo.</span></li>
        </ol>
        <p className="cg-callout">
          <strong>Clients fondateurs, au premier appel :</strong> une phrase suffit, sans détails : « Cette année, nous choisissons quelques associations fondatrices. » Le nombre est vraiment limité, on peut le dire.
        </p>
      </section>

      <section id="objections">
        <h2>Objections au premier appel</h2>
        <div className="cg-table">
          <table>
            <thead>
              <tr><th>Ce qu’on entend</th><th>Réponse</th></tr>
            </thead>
            <tbody>
              <tr><td>« Envoyez-moi de la documentation. »</td><td>« Avec plaisir, je vous l’envoie aujourd’hui. Pour qu’elle vous parle vraiment, on prend 30 minutes la semaine prochaine pour la regarder ensemble avec votre situation? »</td></tr>
              <tr><td>« On n’a pas besoin de ça, ça va bien. »</td><td>« Tant mieux. Qu’est-ce qui fonctionne bien chez vous? » Écouter. Si tout va bien : diagnostic, et rappel avant l’AGA.</td></tr>
              <tr><td>« On a déjà une firme. »</td><td>« Vous en êtes satisfaits? Le contrat se termine quand? » Si insatisfaits : « On peut faire un essai en parallèle pendant votre préavis, sans rien briser. »</td></tr>
              <tr><td>« Combien ça coûte? »</td><td>« Nos prix sont publiés : 6 $ par lot par mois si le conseil s’en occupe avec nos assistants, 17 $ avec en plus un gestionnaire Syndiqia à distance. La rencontre sert à voir ce qui vous convient. »</td></tr>
              <tr><td>« L’IA, on n’a pas confiance. »</td><td>« Vous avez raison d’être prudents. Tout commence en mode confirmation : les assistants préparent, le conseil approuve. Vous voyez tout avant que ça parte. »</td></tr>
              <tr><td>« Ce n’est pas moi qui décide. »</td><td>« Justement : une ou deux personnes du conseil d’abord, puis tout le conseil si ça vaut la peine. »</td></tr>
              <tr><td>« Rappelez-moi plus tard. »</td><td>« Bien sûr. Quel moment serait le meilleur : après votre prochaine réunion du conseil, vers le <Ph>[date]</Ph>? » Noter et rappeler ce jour-là.</td></tr>
              <tr><td>« Comment avez-vous eu mon nom? »</td><td>Dire la vérité : la recommandation, la lettre, la fiche publique du Registraire. Proposer de ne plus appeler si elle le souhaite, et le noter.</td></tr>
            </tbody>
          </table>
        </div>
      </section>

      <section id="messages">
        <h2>Message vocal et courriels</h2>
        <h3>Message vocal (30 secondes au maximum)</h3>
        <Say>
          « Bonjour madame <Ph>[Nom]</Ph>, ici <Ph>[Prénom]</Ph> de Syndiqia, au <Ph>[numéro]</Ph>. <Ph>[Marc, votre déneigeur / Je vous ai écrit la semaine dernière]</Ph> au sujet de l’administration de
          l’association du <Ph>[Domaine X]</Ph>. Je vous rappelle <Ph>[jeudi en fin de journée]</Ph>; vous pouvez aussi me joindre au <Ph>[numéro]</Ph>. Bonne journée! »
        </Say>
        <p className="muted small">Un seul message vocal, puis un courriel. Après deux essais sans réponse, on arrête et on note.</p>
        {MAILS.map((m) => (
          <div key={m.id} className="cg-mail">
            <div className="cg-mail-head">
              <strong>{m.title}</strong>
              <CopyButton text={m.text} />
            </div>
            <pre>{m.text}</pre>
          </div>
        ))}
      </section>

      <section id="qualifier">
        <h2>Après l’appel : qualifier</h2>
        <p>Cocher tout de suite après l’appel, puis noter le résultat dans la fiche du prospect.</p>
        <Score />
      </section>

      <section id="decouverte">
        <h2>La rencontre de découverte (60 minutes)</h2>
        <p>Écouter 40 minutes, montrer 15, conclure 5. Les questions, dans l’ordre :</p>
        <ol>
          <li>« Racontez-moi l’association : combien de lots, ce que vous avez en commun (chemin, plage, chalet, barrière)? »</li>
          <li>« Qui fait quoi au conseil, et combien d’heures par mois, à peu près? »</li>
          <li>« Quels outils utilisez-vous : Excel, courriel, une firme, un logiciel comptable? »</li>
          <li>« Comment se passe la perception des cotisations? Combien de propriétaires en retard, en général? »</li>
          <li>« Comment les propriétaires vous joignent-ils, et combien de questions par mois? »</li>
          <li>« Comment se préparent l’AGA et les procès-verbaux? »</li>
          <li>« Les fournisseurs (déneigement, entretien) : qui les suit, comment arrivent les factures? »</li>
          <li>(Si firme) « Combien payez-vous, qu’est-ce qui est inclus, quand se termine le contrat, quel préavis? »</li>
          <li>« Qui décide d’un changement comme celui-ci : le conseil seul, ou l’assemblée? Quelqu’un de réticent? »</li>
          <li>« Dans un an, qu’est-ce qui vous ferait dire que c’était une bonne décision? » <span className="muted small">(les futurs critères de succès de l’essai)</span></li>
        </ol>
        <p>
          Montrer ensuite, sur la démo la plus proche (Domaine des Hauts-Bois pour un domaine, Les Érables pour un immeuble) : une demande répondue avec un article des règlements, une facture associée à son bon
          de travail, la page des automatisations. Pour le prix, ouvrir le <Link to="/ventes/comparateur">comparateur de coûts</Link>.
        </p>
        <Say>« Est-ce que ça vaudrait la peine de le montrer à tout le conseil? Quelle soirée conviendrait? »</Say>
      </section>

      <section id="references">
        <h2>Demander des références</h2>
        <p>Au mois 2 de l’essai, et à la conversion :</p>
        <Say>
          « Ça fait deux mois; vous m’avez dit que <Ph>[les rappels de cotisations]</Ph> vous ont fait gagner du temps. Est-ce qu’il y a une ou deux associations voisines, ou votre fédération du lac, à qui ça
          pourrait rendre service aussi? Si vous me présentez, et qu’elles deviennent clientes, vous avez un mois gratuit pour chacune. »
        </Say>
        <p className="muted small">Demander une présentation (un courriel où ils nous mettent en copie), pas seulement un nom.</p>
        <h3>S’exercer</h3>
        <p>Avant le premier vrai appel : trois jeux de rôle de 10 minutes, un par situation A, B et E, avec au moins deux objections. Après chaque appel réel, noter en une ligne ce qui a marché et ce qui a bloqué.</p>
      </section>
      <AppFooter />
    </div>
  );
}
